// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

// components
import { useCurrentUserAccess } from '@/hooks/useCurrentUserAccess';
// utils
import { formatNumber } from '@/utils/formatters';
import type { PortfolioReMoniFactor } from '@/utils/remoniScoreCalculator';

const COMPONENT_SCORE_NAMESPACE = 'component-score-descriptions';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function getComponentLabel(metric: PortfolioReMoniFactor, t: TranslateFn) {
  if (!metric.dominantSignal) {
    return null;
  }

  const { resource, component } = metric.dominantSignal;
  return t(`portfolio.highlights.component_labels.${resource}.${component}`);
}

function getDominantSignalExplanation(
  metric: PortfolioReMoniFactor,
  t: TranslateFn,
) {
  if (!metric.dominantSignal) {
    return null;
  }

  const { resource, component, rawValue } = metric.dominantSignal;
  let explanationKey: string | null = null;

  switch (resource) {
    case 'electricity':
      switch (component) {
        case 'outsideKW':
          explanationKey = 'outside_kw';
          break;
        case 'outsideRatio':
          explanationKey = 'outside_ratio';
          break;
        case 'trend':
          explanationKey = 'trend';
          break;
        case 'intensity':
          explanationKey = 'intensity';
          break;
      }
      break;
    case 'heat':
      switch (component) {
        case 'heatingSeason':
          explanationKey = 'heating_season';
          break;
        case 'outsideHeatingSeason':
          explanationKey = 'outside_heating_season';
          break;
        case 'last30':
          explanationKey = 'last30';
          break;
        case 'trend12':
          explanationKey = 'trend12';
          break;
        case 'trend30':
          explanationKey = 'trend30';
          break;
      }
      break;
    case 'water':
      switch (component) {
        case 'outside30':
          explanationKey = 'outside30';
          break;
        case 'trend12':
          explanationKey = 'trend12';
          break;
        case 'trend30':
          explanationKey = 'trend30';
          break;
      }
      break;
  }

  if (!explanationKey) {
    return null;
  }

  return t(`portfolio.highlights.explanations.${resource}.${explanationKey}`, {
    value: formatNumber(rawValue),
  });
}

function getReMoniFactorChipColor(remoniFactor: number) {
  if (remoniFactor >= 75) {
    return 'error';
  }

  if (remoniFactor >= 40) {
    return 'warning';
  }

  return 'success';
}

function getImprovementSuggestions(
  metric: PortfolioReMoniFactor,
  t: TranslateFn,
): string[] {
  if (!metric.dominantSignal) {
    return [];
  }

  const { resource, component } = metric.dominantSignal;
  const suggestions = t(
    `component_scores.${resource}.${component}.improvement_suggestions`,
    {
      ns: COMPONENT_SCORE_NAMESPACE,
      returnObjects: true,
      defaultValue: [],
    },
  ) as unknown;

  return Array.isArray(suggestions) ? suggestions : [];
}

interface RemoniFactorCellProps {
  metric: PortfolioReMoniFactor;
  onOpenDebugDetails: (metric: PortfolioReMoniFactor) => void;
}

const RemoniFactorCell = ({
  metric,
  onOpenDebugDetails,
}: RemoniFactorCellProps) => {
  const { t } = useTranslation(['energy-analysis', COMPONENT_SCORE_NAMESPACE]);
  const { isSuperuser } = useCurrentUserAccess();

  const dominantSignalLabel = getComponentLabel(metric, t);
  const dominantSignalExplanation = getDominantSignalExplanation(metric, t);
  const improvements = getImprovementSuggestions(metric, t);
  const openDebugDetailsLabel = t('portfolio.highlights.open_debug_details');

  const tooltipContent = (
    <Box sx={{ maxWidth: 300 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            gutterBottom
            sx={{ color: 'inherit' }}
          >
            {t('portfolio.highlights.remoni_score')}:{' '}
            {formatNumber(metric.remoniFactor)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'inherit', opacity: 0.9 }}>
            {t('portfolio.highlights.remoni_score_tooltip')}
          </Typography>
        </Box>

        {dominantSignalLabel ? (
          <Box
            sx={{
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              opacity: 0.9,
            }}
          >
            <Typography
              variant="caption"
              display="block"
              gutterBottom
              sx={{ color: 'inherit' }}
            >
              {t('portfolio.highlights.primary_driver_label')}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: 'inherit' }}
            >
              {dominantSignalLabel}
            </Typography>
            {dominantSignalExplanation && (
              <Typography
                variant="caption"
                sx={{
                  color: 'inherit',
                  opacity: 0.85,
                  display: 'block',
                  mt: 0.5,
                }}
              >
                {dominantSignalExplanation}
              </Typography>
            )}
          </Box>
        ) : null}

        {improvements.length > 0 && (
          <Box
            sx={{
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              opacity: 0.9,
            }}
          >
            <Typography
              variant="caption"
              display="block"
              gutterBottom
              sx={{ color: 'inherit', fontWeight: 600 }}
            >
              {t('portfolio.highlights.improvements_label')}
            </Typography>
            <Stack
              component="ul"
              spacing={0.5}
              sx={{ pl: 2, m: 0, color: 'inherit' }}
            >
              {improvements.map((improvement, index) => (
                <Typography
                  key={index}
                  component="li"
                  variant="caption"
                  sx={{ color: 'inherit', opacity: 0.85 }}
                >
                  {improvement}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );

  return (
    <Box display="flex" justifyContent="flex-end" width="100%">
      <Tooltip
        title={tooltipContent}
        slotProps={{
          tooltip: {
            sx: {
              maxHeight: 400,
              overflowY: 'auto',
            },
          },
        }}
      >
        <Chip
          data-testid={`remoni-score-chip-${metric.matrikelKey}`}
          color={getReMoniFactorChipColor(metric.remoniFactor)}
          label={formatNumber(metric.remoniFactor)}
          size="small"
          aria-label={isSuperuser ? openDebugDetailsLabel : undefined}
          onClick={
            isSuperuser
              ? (event) => {
                  event.stopPropagation();
                  onOpenDebugDetails(metric);
                }
              : undefined
          }
          sx={{
            fontWeight: 700,
            minWidth: 64,
            cursor: isSuperuser ? 'pointer' : 'default',
          }}
        />
      </Tooltip>
    </Box>
  );
};

export default RemoniFactorCell;
